import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, prospects as prospectsApi } from '../api/client.ts';
import { PROSPECT_STATUS_LABEL } from '../api/types.ts';
import type { DiscoveryInput, DiscoveryMath, Prospect as ProspectRow } from '../api/types.ts';
import { Alert, Button, Spinner } from '../ui/primitives.tsx';
import { Logo, useDocumentTitle } from '../ui/Logo.tsx';

/**
 * Depois do primeiro contato: retomar na data certa, registrar o que o dono
 * respondeu na reunião e transformar isso em proposta.
 *
 * Tudo aqui é INFORMADO pelo dono, não coletado (decisão 35). A proposta diz
 * isso ao lado de cada número.
 */

// --- Dinheiro -----------------------------------------------------------------

/** "45", "45,90", "1.234,50" → centavos. Vazio ou inválido → `undefined`. */
export function parseMoney(text: string): number | undefined {
  const clean = text.trim().replace(/^R\$\s*/i, '');
  if (clean === '') return undefined;
  const normalized = clean.includes(',') ? clean.replace(/\./g, '').replace(',', '.') : clean;
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : undefined;
}

export function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function centsToInput(cents: number | undefined): string {
  return cents === undefined ? '' : (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function parseCount(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const value = Number(text);
  return Number.isInteger(value) && value >= 0 ? value : undefined;
}

function parsePct(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const value = Number(text.replace(',', '.'));
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : undefined;
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

/** Hoje + N dias, "AAAA-MM-DD", no fuso do navegador (é o de quem opera). */
function isoInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const input =
  'w-full rounded-[var(--radius-sm)] border border-[var(--color-line)] bg-[var(--color-surface)] px-2 py-1.5 text-sm';

// --- Retomar contato ----------------------------------------------------------

export function FollowUpPanel({ prospect }: { prospect: ProspectRow }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(prospect.follow_up_at ?? '');
  const [note, setNote] = useState(prospect.follow_up_note ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDate(prospect.follow_up_at ?? '');
    setNote(prospect.follow_up_note ?? '');
  }, [prospect.follow_up_at, prospect.follow_up_note]);

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['prospect', prospect.id] });
    void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
  };
  const onError = (err: unknown) => setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');

  const save = useMutation({
    mutationFn: () => prospectsApi.setFollowUp(prospect.id, date, note.trim() || undefined),
    onSuccess: refresh,
    onError,
  });
  const clear = useMutation({ mutationFn: () => prospectsApi.clearFollowUp(prospect.id), onSuccess: refresh, onError });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!date) {
      setError('Escolha a data.');
      return;
    }
    save.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 text-sm">
      {prospect.follow_up_at && (
        <p className="text-xs text-[var(--color-ink-soft)]">
          Marcado para <strong>{formatDay(prospect.follow_up_at)}</strong>
          {prospect.follow_up_note ? ` · ${prospect.follow_up_note}` : ''}
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        {[
          ['Em 7 dias', 7],
          ['Em 15 dias', 15],
          ['Em 30 dias', 30],
        ].map(([label, days]) => (
          <Button key={label} type="button" variant="ghost" onClick={() => setDate(isoInDays(Number(days)))}>
            {label}
          </Button>
        ))}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-[var(--color-ink-soft)]">Data</span>
        <input id="follow-up-date" type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-[var(--color-ink-soft)]">Por que retomar (opcional)</span>
        <input
          id="follow-up-note"
          className={input}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ex.: pediu para voltar depois do Dia das Crianças"
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={save.isPending}>
          {prospect.follow_up_at ? 'Remarcar' : 'Marcar retomada'}
        </Button>
        {prospect.follow_up_at && (
          <Button type="button" variant="ghost" loading={clear.isPending} onClick={() => clear.mutate()}>
            Concluir
          </Button>
        )}
      </div>
      {error && <p className="text-xs text-[var(--color-danger)]">{error}</p>}
    </form>
  );
}

/** Lista do painel: hoje, atrasadas e os próximos 7 dias. */
export function FollowUpsList() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['follow-ups'],
    queryFn: () => prospectsApi.followUps(7),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert>Não foi possível carregar as retomadas.</Alert>;
  const items = data?.followUps ?? [];
  if (items.length === 0) {
    return <p className="text-sm text-[var(--color-ink-soft)]">Nenhuma retomada para hoje nem para os próximos 7 dias.</p>;
  }
  return (
    <ul className="flex flex-col divide-y divide-[var(--color-line)] text-sm">
      {items.map((item) => (
        <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
          <span className="min-w-0">
            <Link to={`/prospects/${item.id}`} className="font-medium text-[var(--color-brand-text)] hover:underline">
              {item.name}
            </Link>
            <span className="text-xs text-[var(--color-ink-soft)]">
              {' '}
              · {PROSPECT_STATUS_LABEL[item.status]}
              {item.follow_up_note ? ` · ${item.follow_up_note}` : ''}
            </span>
          </span>
          <span
            className={`tabular text-xs ${
              item.days_overdue > 0
                ? 'text-[var(--color-danger)]'
                : item.days_overdue === 0
                  ? 'font-medium text-[var(--color-ink)]'
                  : 'text-[var(--color-ink-soft)]'
            }`}
          >
            {item.days_overdue > 0
              ? `${item.days_overdue} ${item.days_overdue === 1 ? 'dia' : 'dias'} de atraso`
              : item.days_overdue === 0
                ? 'hoje'
                : formatDay(item.follow_up_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// --- Diagnóstico da reunião ---------------------------------------------------

interface DiscoveryForm {
  productId: string | null;
  answers: Record<string, string>;
  ticket: string;
  commission: string;
  appOrders: string;
  peak: string;
  setup: string;
  monthly: string;
  offerNotes: string;
}

export function DiscoveryPanel({ prospectId }: { prospectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['discovery', prospectId],
    queryFn: () => prospectsApi.discovery(prospectId),
  });
  const [form, setForm] = useState<DiscoveryForm | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [math, setMath] = useState<DiscoveryMath>({});

  useEffect(() => {
    if (!data) return;
    const saved = data.discovery;
    setMath(data.math);
    setForm({
      productId: data.productId,
      answers: Object.fromEntries((saved?.answers ?? []).map((entry) => [entry.question, entry.answer])),
      ticket: centsToInput(saved?.numbers.ticketCents),
      commission: saved?.numbers.appCommissionPct?.toString().replace('.', ',') ?? '',
      appOrders: saved?.numbers.appOrdersPerMonth?.toString() ?? '',
      peak: saved?.numbers.peakOrders?.toString() ?? '',
      setup: centsToInput(saved?.offer.setupCents),
      monthly: centsToInput(saved?.offer.monthlyCents),
      offerNotes: saved?.offer.notes ?? '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: (payload: DiscoveryInput) => prospectsApi.saveDiscovery(prospectId, payload),
    onSuccess: (result) => {
      setMath(result.math);
      setMessage({ tone: 'ok', text: 'Diagnóstico salvo.' });
      void queryClient.invalidateQueries({ queryKey: ['discovery', prospectId] });
      void queryClient.invalidateQueries({ queryKey: ['prospect', prospectId] });
    },
    onError: (err: unknown) =>
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'Não foi possível salvar.' }),
  });

  if (isLoading || !form) return error ? <Alert>Não foi possível carregar o diagnóstico.</Alert> : <Spinner />;

  const product = data?.products.find((entry) => entry.id === form.productId);
  // Perguntas do produto primeiro; respostas salvas de perguntas que saíram do
  // catálogo continuam aparecendo, para não sumirem em silêncio.
  const questions = [
    ...(product?.questions ?? []),
    ...Object.keys(form.answers).filter((question) => !(product?.questions ?? []).includes(question)),
  ];

  const set = <K extends keyof DiscoveryForm>(key: K, value: DiscoveryForm[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setMessage(null);
    const numbers = {
      ticketCents: parseMoney(form.ticket),
      appCommissionPct: parsePct(form.commission),
      appOrdersPerMonth: parseCount(form.appOrders),
      peakOrders: parseCount(form.peak),
    };
    const offer = {
      setupCents: parseMoney(form.setup),
      monthlyCents: parseMoney(form.monthly),
      notes: form.offerNotes.trim() || undefined,
    };
    const strip = <T extends object>(value: T) =>
      Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
    save.mutate({
      productId: form.productId,
      answers: questions.map((question) => ({ question, answer: (form.answers[question] ?? '').trim() })),
      numbers: strip(numbers),
      offer: strip(offer),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-sm">
      <p className="text-xs text-[var(--color-ink-soft)]">
        O que o dono respondeu na conversa. É informação dele, não verificada, e a proposta mostra assim.
        {data?.discovery?.updated_by_name &&
          ` Última edição: ${data.discovery.updated_by_name}, ${new Date(data.discovery.updated_at).toLocaleString('pt-BR')}.`}
      </p>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-[var(--color-ink-soft)]">Produto discutido</span>
        <select
          id="discovery-product"
          className={input}
          value={form.productId ?? ''}
          onChange={(e) => set('productId', e.target.value || null)}
        >
          {data?.products.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 font-display text-sm font-semibold text-[var(--color-ink)]">
          Perguntas
        </legend>
        {questions.length === 0 && (
          <p className="text-xs text-[var(--color-ink-soft)]">Este produto não tem perguntas no catálogo.</p>
        )}
        {questions.map((question, index) => (
          <label key={question} className="flex flex-col gap-1">
            <span>{question}</span>
            <textarea
              id={`discovery-answer-${index}`}
              rows={2}
              className={input}
              value={form.answers[question] ?? ''}
              onChange={(e) => set('answers', { ...form.answers, [question]: e.target.value })}
            />
          </label>
        ))}
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 font-display text-sm font-semibold text-[var(--color-ink)]">
          Números da operação
        </legend>
        <NumberField id="discovery-ticket" label="Ticket médio (R$)" value={form.ticket} onChange={(v) => set('ticket', v)} placeholder="45,00" />
        <NumberField id="discovery-commission" label="Comissão do app (%)" value={form.commission} onChange={(v) => set('commission', v)} placeholder="23" />
        <NumberField id="discovery-app-orders" label="Pedidos por mês no app" value={form.appOrders} onChange={(v) => set('appOrders', v)} placeholder="600" />
        <NumberField id="discovery-peak" label="Pedidos numa noite de pico" value={form.peak} onChange={(v) => set('peak', v)} placeholder="80" />
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-2 font-display text-sm font-semibold text-[var(--color-ink)]">
          Oferta apresentada
        </legend>
        <NumberField id="discovery-setup" label="Implantação (R$)" value={form.setup} onChange={(v) => set('setup', v)} placeholder="1.000,00" />
        <NumberField id="discovery-monthly" label="Mensalidade (R$)" value={form.monthly} onChange={(v) => set('monthly', v)} placeholder="200,00" />
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs text-[var(--color-ink-soft)]">Condições (opcional)</span>
          <textarea
            id="discovery-offer-notes"
            rows={2}
            className={input}
            value={form.offerNotes}
            onChange={(e) => set('offerNotes', e.target.value)}
            placeholder="Ex.: vaga de cliente fundador, mensalidade travada por 12 meses"
          />
        </label>
      </fieldset>

      <MathSummary math={math} />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" loading={save.isPending}>
          Salvar diagnóstico
        </Button>
        {data?.discovery && (
          <Link
            to={`/prospects/${prospectId}/proposta`}
            className="text-sm text-[var(--color-brand-text)] hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Abrir proposta para imprimir
          </Link>
        )}
        {message && (
          <span className={`text-xs ${message.tone === 'ok' ? 'text-[var(--color-ok)]' : 'text-[var(--color-danger)]'}`}>
            {message.text}
          </span>
        )}
      </div>
    </form>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-[var(--color-ink-soft)]">{label}</span>
      <input id={id} inputMode="decimal" className={input} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

function MathSummary({ math }: { math: DiscoveryMath }) {
  if (math.commissionPerOrderCents === undefined) {
    return (
      <p className="text-xs text-[var(--color-ink-soft)]">
        Com ticket médio e comissão salvos, a conta da comissão aparece aqui e na proposta.
      </p>
    );
  }
  return (
    <div className="grid gap-2 rounded-[var(--radius-sm)] bg-[var(--color-canvas)] p-3 sm:grid-cols-3">
      <Stat label="Comissão por pedido" value={formatMoney(math.commissionPerOrderCents)} />
      {math.commissionPerMonthCents !== undefined && (
        <Stat label="Comissão por mês" value={formatMoney(math.commissionPerMonthCents)} />
      )}
      {math.ordersToPayMonthly !== undefined && (
        <Stat
          label="Pedidos por mês fora do app que pagam a mensalidade"
          value={`${math.ordersToPayMonthly} (${(math.ordersToPayMonthly / 30).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}/dia)`}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-[var(--color-ink-soft)]">{label}</span>
      <span className="tabular text-base font-semibold">{value}</span>
    </div>
  );
}

// --- Proposta -----------------------------------------------------------------

/**
 * A proposta, em página própria e sem o menu do painel: é para imprimir ou
 * salvar em PDF pelo navegador e mandar ao dono.
 */
export function ProposalPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['proposal', id],
    queryFn: () => prospectsApi.proposal(id!),
    enabled: Boolean(id),
  });

  useDocumentTitle(data ? `Proposta para ${data.prospect.name}` : 'Proposta');

  if (isLoading) {
    return (
      <div className="flex justify-center py-16 text-[var(--color-ink-faint)]">
        <Spinner label="Montando a proposta" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Alert>{error instanceof ApiError ? error.message : 'Não foi possível montar a proposta.'}</Alert>
      </div>
    );
  }

  const { company, prospect, product, discovery, math } = data;
  const offer = discovery.offer;
  const numbers = discovery.numbers;
  const today = new Date().toLocaleDateString('pt-BR');

  // A marca da Pure Layout só vai na proposta do perfil Pure Layout: outro
  // perfil do mesmo login vende com o nome dele, e o logo seria de outra empresa.
  const isPureLayout = /pure\s*layout/i.test(company.name);

  return (
    // Ilha clara (`.theme-paper`): a proposta vai para o papel e para o dono da loja, e o painel escuro não.
    <div className="theme-paper min-h-full">
    <div className="mx-auto flex max-w-3xl flex-col gap-8 bg-white pb-12 text-[15px] leading-relaxed text-[var(--color-ink)] print:pb-0">
      <div className="flex justify-end gap-2 px-8 pt-6 print:hidden">
        <Button variant="primary" onClick={() => window.print()}>
          Imprimir ou salvar em PDF
        </Button>
      </div>

      <header className="on-night relative overflow-hidden bg-[var(--color-night)] px-8 py-10 text-white [print-color-adjust:exact] sm:px-12">
        {isPureLayout && (
          <img
            src="/brand/pl-mark-192.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -bottom-16 w-72 opacity-[0.08] select-none"
          />
        )}
        <div className="relative flex items-start justify-between gap-6">
          {isPureLayout ? (
            <Logo size={28} product="Soluções em tecnologia" />
          ) : (
            <span className="font-display text-lg font-semibold">{company.name}</span>
          )}
          <span className="text-right text-xs text-[var(--color-night-soft)]">
            Proposta comercial
            <br />
            {today}
          </span>
        </div>
        <h1 className="relative mt-12 font-serif text-[44px] leading-[1.05] font-normal italic">
          {product?.name ?? 'Proposta comercial'}
        </h1>
        <div className="relative mt-8 border-l-2 border-[var(--color-cyan)] pl-4">
          <p className="text-xs text-[var(--color-night-soft)]">Preparada para</p>
          <p className="font-display text-xl font-semibold">{prospect.name}</p>
          {prospect.city && (
            <p className="text-sm text-[var(--color-night-soft)]">
              {prospect.city}
              {prospect.state ? `/${prospect.state}` : ''}
            </p>
          )}
        </div>
      </header>
      <div className="flex flex-col gap-8 px-8 sm:px-12">

      {discovery.answers.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">O que conversamos</h2>
          <dl className="flex flex-col gap-3">
            {discovery.answers.map((entry) => (
              <div key={entry.question}>
                <dt className="text-sm text-neutral-500">{entry.question}</dt>
                <dd>{entry.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {math.commissionPerOrderCents !== undefined && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">A conta da comissão</h2>
          <table className="w-full text-left text-sm">
            <tbody className="divide-y divide-neutral-200">
              <tr>
                <td className="py-2 text-neutral-600">Ticket médio</td>
                <td className="py-2 text-right tabular">{formatMoney(numbers.ticketCents!)}</td>
              </tr>
              <tr>
                <td className="py-2 text-neutral-600">Comissão do app</td>
                <td className="py-2 text-right tabular">{numbers.appCommissionPct!.toLocaleString('pt-BR')}%</td>
              </tr>
              <tr>
                <td className="py-2 text-neutral-600">Comissão em cada pedido</td>
                <td className="py-2 text-right font-semibold tabular">{formatMoney(math.commissionPerOrderCents)}</td>
              </tr>
              {math.commissionPerMonthCents !== undefined && (
                <tr>
                  <td className="py-2 text-neutral-600">
                    Comissão por mês ({numbers.appOrdersPerMonth!.toLocaleString('pt-BR')} pedidos)
                  </td>
                  <td className="py-2 text-right font-semibold tabular">{formatMoney(math.commissionPerMonthCents)}</td>
                </tr>
              )}
              {math.ordersToPayMonthly !== undefined && (
                <tr>
                  <td className="py-2 text-neutral-600">Pedidos por mês fora do app que pagam a mensalidade</td>
                  <td className="py-2 text-right font-semibold tabular">{math.ordersToPayMonthly}</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="text-xs text-neutral-500">Números informados por vocês na nossa conversa.</p>
        </section>
      )}

      {product && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">A solução</h2>
          {product.description && <p>{product.description}</p>}
          {product.salesArguments.length > 0 && (
            <ul className="list-disc pl-5">
              {product.salesArguments.map((argument) => (
                <li key={argument}>{argument}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {(offer.setupCents !== undefined || offer.monthlyCents !== undefined || offer.notes) && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Investimento</h2>
          <div className="flex flex-wrap gap-8">
            {offer.setupCents !== undefined && (
              <div>
                <span className="block text-sm text-neutral-500">Implantação</span>
                <span className="text-2xl font-semibold tabular">{formatMoney(offer.setupCents)}</span>
              </div>
            )}
            {offer.monthlyCents !== undefined && (
              <div>
                <span className="block text-sm text-neutral-500">Mensalidade</span>
                <span className="text-2xl font-semibold tabular">{formatMoney(offer.monthlyCents)}</span>
              </div>
            )}
          </div>
          {offer.notes && <p className="whitespace-pre-line">{offer.notes}</p>}
        </section>
      )}

      <footer className="border-t border-neutral-300 pt-6 text-sm text-neutral-600">
        <p>
          {company.signer} · {company.name}
        </p>
      </footer>
      </div>
    </div>
    </div>
  );
}
